import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useTranslation } from 'react-i18next';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { useGetLanguages } from 'services/enums';
import { IVideoMutationForm } from 'services/video/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLanguageSelectOptions } from 'utils/global/language.utils';

const VideoTitles = () => {
  const {
    values,
    status,
    setFieldValue,
  }: IFormikContextType<IVideoMutationForm> =
    useFormikContext<IVideoMutationForm>();
  const { t } = useTranslation();

  const languages = useGetLanguages();

  const setNewTitle = () =>
    setFieldValue(`otherTitles[${values.otherTitles.length}]`, {
      id: values.otherTitles.length,
      language: -1,
      title: '',
      version: '',
    });

  const removeTitle = (id: number) =>
    setFieldValue(
      'otherTitles',
      values.otherTitles
        .filter((_, key) => key !== id)
        .map((title, key) => ({ ...title, id: key }))
    );

  return (
    <Table
      id={'VideoTitles'}
      size='compact'
      data={{
        nodes: values.otherTitles.map((title, key) => ({
          ...title,
          id: key,
        })),
      }}
      columns={[
        {
          label: t('input.label.title'),
          renderCell: ({ title, id }) => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text text={title} />
              ) : (
                <FormInput.Text
                  name={`otherTitles[${id}].title`}
                  label={t('input.label.title')}
                />
              )}
            </TableItem>
          ),
          resize: true,
        },
        {
          label: t('input.label.language'),
          renderCell: ({ language, id }) => (
            <TableItem className='visible overflow-visible'>
              {status?.displayMode ? (
                <TableItem.Text text={t(`enum.LANGUAGE.${language}`)} />
              ) : (
                <FormInput.Select
                  name={`otherTitles[${id}].language`}
                  label={t('input.label.language')}
                  value={{
                    value: values.otherTitles[id].language,
                    label: t(`enum.LANGUAGE.${language}`),
                  }}
                  options={getLanguageSelectOptions(languages.data, [
                    ...values.otherTitles,
                    values.mainTitle,
                  ])}
                />
              )}
            </TableItem>
          ),
          resize: true,
        },
        {
          label: t('input.label.version'),
          renderCell: ({ version, id }) => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text text={version} />
              ) : (
                <FormInput.Text
                  name={`otherTitles[${id}].version`}
                  label={t('input.label.version')}
                />
              )}
            </TableItem>
          ),
        },
        {
          label: '',
          renderCell: ({ id }) => (
            <TableItem justifyPosition='end'>
              {status?.displayMode ? null : (
                <Button
                  variant={BUTTON_VARIANT.remove}
                  onlyIcon
                  isClear
                  onClick={() => removeTitle(id)}>
                                    <Button.Description description={t('button.remove')} />

                                    <Button.Icon icon={<PiTrash />} />
                </Button>
              )}
            </TableItem>
          ),
        },
      ]}
      tableOptions={
        status?.displayMode
          ? undefined
          : {
              renderAfterTable: () => (
                <tfoot className='full-width-footer'>
                  <TableMessage
                    className='cursor-pointer'
                    onClick={setNewTitle}>
                    <TableItem.Text text={t('button.add')} />
                    <PiPlus />
                  </TableMessage>
                </tfoot>
              ),
            }
      }
    />
  );
};

export default VideoTitles;
